import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatOption, MatSelect} from '@angular/material/select';
import {HttpErrorResponse} from '@angular/common/http';

import {NGXLogger} from 'ngx-logger';
import {Language} from '@twpub/core/models';
import {SuggestionService} from '@twpub/core/services/suggestion.service';
import {DictionaryService} from '@twpub/core/services/dictionary.service';

export interface SuggestTermDialogData {
  dictionaries: { id: number; name: string }[];
  defaultDictionaryId?: number;
  defaultLanguageCode?: string;
}

@Component({
  selector: 'pub-suggest-term-dialog',
  templateUrl: './suggest-term-dialog.component.html',
  styleUrls: ['./suggest-term-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle, MatDialogContent, MatDialogActions, ReactiveFormsModule, MatFormField, MatLabel,
    MatInput, MatError, MatButton, MatProgressSpinner, MatIcon, MatSelect, MatOption
  ]
})
export class SuggestTermDialogComponent implements OnInit {
  suggestForm: FormGroup;
  submitting = false;
  duplicateWarning: string | null = null;
  languages: Language[] = [];

  // i18n labels
  titleLabel = $localize`:@@suggestion.title:Suggest a Term`;
  termNameLabel = $localize`:@@suggestion.termName:New term`;
  dictionaryLabel = $localize`:@@suggestion.dictionary:Dictionary`;
  languageLabel = $localize`:@@suggestion.language:Language`;
  submitLabel = $localize`:@@suggestion.submit:Submit`;
  cancelLabel = $localize`:@@suggestion.cancel:Cancel`;
  requiredError = $localize`:@@suggestion.error.required:This field is required`;
  successMessage = $localize`:@@suggestion.success:Thank you! Your term suggestion has been submitted.`;
  errorMessage = $localize`:@@suggestion.error:Failed to submit suggestion. Please try again later.`;

  get showDictionarySelect(): boolean {
    return this.data.dictionaries.length > 1;
  }

  constructor(
    private logger: NGXLogger,
    private fb: FormBuilder,
    private suggestionService: SuggestionService,
    private dictionaryService: DictionaryService,
    @Inject(MAT_DIALOG_DATA) public data: SuggestTermDialogData,
    private dialogRef: MatDialogRef<SuggestTermDialogComponent>,
    private snackBar: MatSnackBar
  ) {
    this.suggestForm = this.fb.group({
      dictionaryId: [null, Validators.required],
      termName: ['', Validators.required],
      languageCode: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    const dicts = this.data.dictionaries;
    const initialDictId = (this.data.defaultDictionaryId != null
      && dicts.some(d => d.id === this.data.defaultDictionaryId))
        ? this.data.defaultDictionaryId
        : dicts[0]?.id;
    this.suggestForm.patchValue({dictionaryId: initialDictId});
    this.loadLanguages(initialDictId);
    this.suggestForm.get('dictionaryId')!.valueChanges.subscribe(id => this.loadLanguages(id));
  }

  private loadLanguages(dictionaryId?: number): void {
    if (dictionaryId == null) {
      this.languages = [];
      return;
    }
    this.dictionaryService.getLanguages([dictionaryId]).subscribe(langs => {
      this.languages = langs;
      const current = this.suggestForm.get('languageCode')!.value;
      const keep = (this.data.defaultLanguageCode && langs.some(l => l.code === this.data.defaultLanguageCode))
        ? this.data.defaultLanguageCode
        : (langs.some(l => l.code === current) ? current : (langs.length === 1 ? langs[0].code : ''));
      this.suggestForm.patchValue({languageCode: keep});
    });
  }

  submit(): void {
    if (this.suggestForm.invalid) {
      this.suggestForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.duplicateWarning = null;
    const {termName, languageCode, dictionaryId} = this.suggestForm.value;

    this.suggestionService.submit({termName, languageCode, dictionaryId}).subscribe({
      next: () => {
        this.submitting = false;
        this.snackBar.open(this.successMessage, undefined, {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['snackbar-success']
        });
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        if (err.status === 409) {
          // Duplicate suggestion
          const body = err.error;
          const status = body?.status || 'PENDING';
          const comment = body?.reviewComment;
          const statusLabel = $localize`:@@suggestion.duplicate.status:Status`;
          const commentLabel = $localize`:@@suggestion.duplicate.comment:Comment`;
          let msg = $localize`:@@suggestion.duplicate:This term has already been suggested.` + ` ${statusLabel}: ${status}.`;
          if (comment) {
            msg += ` ${commentLabel}: ${comment}`;
          }
          this.duplicateWarning = msg;
        } else {
          this.logger.error('Error submitting suggestion:', err);
          this.snackBar.open(this.errorMessage, undefined, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['snackbar-error']
          });
        }
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
